import * as React from "react";
import { Collapse } from "react-bootstrap";

import ChromeIconImg from "@skv/assets/gfx/browser/chrome.png";
import EdgeIconImg from "@skv/assets/gfx/browser/edge.png";
import UpgradeFeedback from "@skv/assets/gfx/feedback/upgrade.png";
import { ExternalLink, SvgSprite } from "@skv/core";

export const BrowserUpgrade = (): JSX.Element => {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="container d-flex flex-column align-items-center pt-5 pb-4">
      <img
        src={UpgradeFeedback}
        className="img-fluid mb-4"
        alt=""
        width={138}
        height={138}
      />
      <h1 className="mb-4">Time to upgrade your browser.</h1>
      <div className="row">
        <div className="col-md-7">
          <h2 className="h5">Why am I seeing this page?</h2>
          <p>
            You&apos;re seeing this page because your browser (the program you
            use to browse the web) needs to be upgraded.
          </p>
          <h2 className="h5">Why do I need to upgrade?</h2>
          <p>
            If you&apos;re using an outdated browser it may prevent you from
            being able to see or access all the functionality on our website.
          </p>

          <button
            type="button"
            className="btn btn-link p-0 border-0 mb-3"
            onClick={(e) => {
              e.preventDefault();
              setOpen((prev) => !prev);
            }}
          >
            <SvgSprite
              id="icons:chev-down"
              width={16}
              height={16}
              className="mr-2"
              style={{
                transform: open
                  ? "rotate3d(1, 0, 0, 180deg)"
                  : "rotate3d(1, 0, 0, 0deg)",
              }}
              css={`
                transition: all 0.3s ${(p) => p.theme.timingFnEaseOut};
              `}
            />
            Learn more
          </button>
          <Collapse in={open}>
            <div>
              <p>
                Here are 3 reasons why you need to update your browser or switch
                to a newer one:
              </p>
              <ol>
                <li>
                  <strong>Website compatibility</strong> - new features are
                  often only supported by newer browsers and when websites
                  utilise these features, older browsers don&apos;t function
                  properly.
                </li>
                <li>
                  <strong>Security risks</strong> - newer browsers are
                  continually updated to fix security issues so using an
                  outdated browser can make you more vulnerable.
                </li>
                <li>
                  <strong>A faster browsing experience</strong> - older browsers
                  do not perform at the same speed as newer browsers. This makes
                  your experience considerably slower and can sometimes cause
                  your browser to crash or freeze.
                </li>
              </ol>
            </div>
          </Collapse>

          <h2 className="h5">What about Internet Explorer?</h2>
          <p>
            <ExternalLink href="https://techcommunity.microsoft.com/t5/microsoft-365-blog/microsoft-365-apps-say-farewell-to-internet-explorer-11-and/ba-p/1591666">
              Internet Explorer is no longer supported by Microsoft
            </ExternalLink>{" "}
            and they are encouraging people to switch to their new browser
            called Edge.
          </p>
        </div>
        <aside className="col-md-4 ml-md-auto">
          <h2 className="h5">How do I upgrade?</h2>
          <p>
            Upgrading is <strong>free and easy</strong>, and ensures that SEEK
            Volunteer and other websites run more efficiently (they will look
            better too!).
          </p>
          <p>
            Make sure you are using the most recent version of your browser or
            click on the links below to see further information on how to
            upgrade:
          </p>
          <ul className="list-unstyled">
            <li className="mb-3">
              <ExternalLink href="https://www.microsoft.com/edge">
                <img
                  css={`
                    width: 24px;
                    vertical-align: top;
                  `}
                  className="mr-2"
                  src={EdgeIconImg}
                  alt="Microsoft Edge icon"
                />
                Microsoft Edge
              </ExternalLink>
            </li>
            <li>
              <ExternalLink href="https://www.google.com/chrome">
                <img
                  css={`
                    width: 24px;
                    vertical-align: top;
                  `}
                  className="mr-2"
                  src={ChromeIconImg}
                  alt="Google Chrome icon"
                />
                Google Chrome
              </ExternalLink>
            </li>
          </ul>
        </aside>
      </div>
    </div>
  );
};
